import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ShieldCheck, MapPin, Syringe, Droplets, Activity, Stethoscope, ArrowRight, Instagram, Menu, X } from 'lucide-react';
// --- Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.8,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const SectionLabel = ({
  text
}: {
  text: string;
}) => <span className="text-gold uppercase tracking-[0.2em] text-xs font-bold mb-6 block" data-id="element-1">
    {text}
  </span>;
const Monogram = ({
  className = ''
}: {
  className?: string;
}) => <div className={`w-12 h-12 rounded-full bg-forest border border-gold flex items-center justify-center text-gold font-serif text-xl ${className}`} data-id="element-2">
    CF
  </div>;
const ServiceCard = ({
  service
}: {
  service: any;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return <div className="group bg-forest border border-gold/20 hover:border-gold/60 p-8 rounded-xl transition-all duration-300 h-full flex flex-col" data-id="element-3">
      <service.icon className="w-8 h-8 text-gold mb-6 stroke-[1.5]" data-id="element-4" />
      <h3 className="font-serif text-2xl text-gold mb-4" data-id="element-5">{service.title}</h3>
      <p className="text-ivory/70 font-light leading-relaxed" data-id="element-6">{service.desc}</p>

      <AnimatePresence data-id="element-7">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0,
        marginTop: 0
      }} animate={{
        height: 'auto',
        opacity: 1,
        marginTop: 16
      }} exit={{
        height: 0,
        opacity: 0,
        marginTop: 0
      }} className="overflow-hidden" data-id="element-8">
            <p className="text-ivory/60 font-light text-sm leading-relaxed border-t border-gold/10 pt-4" data-id="element-9">
              {service.fullDesc}
            </p>
          </motion.div>}
      </AnimatePresence>

      <div className="mt-auto pt-6" data-id="element-10">
        <button onClick={() => setIsExpanded(!isExpanded)} className="text-gold text-xs uppercase tracking-widest font-bold hover:text-ivory transition-colors flex items-center gap-1" data-id="element-11">
          {isExpanded ? 'Ver menos' : 'Leer más'}
          <motion.span animate={{
          rotate: isExpanded ? 180 : 0
        }} className="inline-block" data-id="element-12">
            ↓
          </motion.span>
        </button>
      </div>
    </div>;
};
// --- Main App ---
export function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-ivory selection:bg-gold/30" data-id="element-13">
      {/* Navigation */}
      <nav className={`sticky top-0 w-full z-50 transition-all duration-500 bg-forest/95 backdrop-blur-sm border-b border-gold/10 ${isScrolled ? 'py-2' : 'py-3'}`} data-id="element-14">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-15">
          <div className="flex items-center gap-3" data-id="element-16">
            <Monogram className="w-8 h-8 text-sm" data-id="element-17" />
            <span className={`font-serif text-base hidden sm:block text-ivory`} data-id="element-18">
              Dra. Carla Fierro
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8" data-id="element-19">
            <a href="#sobre-mi" className="text-ivory/90 hover:text-gold transition-colors text-sm uppercase tracking-wider" data-id="element-20">
              Sobre mí
            </a>
            <a href="#tratamientos" className="text-ivory/90 hover:text-gold transition-colors text-sm uppercase tracking-wider" data-id="element-21">
              Tratamientos
            </a>
            <a href="#contacto" className="text-ivory/90 hover:text-gold transition-colors text-sm uppercase tracking-wider" data-id="element-22">
              Contacto
            </a>
            <button className="bg-gold text-forest px-5 py-2 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-ivory transition-colors" data-id="element-23">
              Agendar
            </button>
          </div>

          <button className="md:hidden text-ivory" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-24">
            {mobileMenuOpen ? <X data-id="element-25" /> : <Menu data-id="element-26" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-forest border-t border-gold/20 p-6 flex flex-col gap-4 md:hidden" data-id="element-27">
            <a href="#sobre-mi" onClick={() => setMobileMenuOpen(false)} className="text-ivory hover:text-gold uppercase tracking-wider text-sm" data-id="element-28">
              Sobre mí
            </a>
            <a href="#tratamientos" onClick={() => setMobileMenuOpen(false)} className="text-ivory hover:text-gold uppercase tracking-wider text-sm" data-id="element-29">
              Tratamientos
            </a>
            <a href="#contacto" onClick={() => setMobileMenuOpen(false)} className="text-ivory hover:text-gold uppercase tracking-wider text-sm" data-id="element-30">
              Contacto
            </a>
          </div>}
      </nav>

      {/* Hero Banner Image */}
      <section className="relative w-full aspect-[16/9] md:aspect-[21/9] lg:aspect-[2.5/1]" data-id="element-31">
        <div className="absolute inset-0 bg-cover bg-top bg-no-repeat" style={{
        backgroundImage: `url('https://cdn.magicpatterns.com/uploads/aXAfpY6bYJgED5Y9UC9Vte/banner_2.png')`
      }} data-id="element-32">
          {/* Subtle gradient to blend into the forest green section below, and darken top for navbar */}
          <div className="absolute inset-0 bg-gradient-to-b from-forest/60 via-transparent to-forest" data-id="element-33"></div>
        </div>
      </section>

      {/* Hero Text Content (Below Banner) */}
      <section className="bg-forest relative pb-16 pt-8 flex items-center justify-center overflow-hidden" data-id="element-34">
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center flex flex-col items-center" data-id="element-35">
          <FadeIn delay={0.2} data-id="element-36">
            <Monogram className="w-12 h-12 text-xl mb-6 mx-auto" data-id="element-37" />
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-38">
            <h1 className="font-serif text-4xl md:text-5xl text-ivory leading-tight mb-4 text-balance" data-id="element-39">
              Resultados Naturales.
              <br data-id="element-40" />
              <span className="italic text-gold" data-id="element-41">Belleza Armónica.</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.6} data-id="element-42">
            <p className="text-ivory/80 text-base md:text-lg font-light max-w-2xl mx-auto mb-8 text-balance" data-id="element-43">
              Medicina Estética de precisión para quienes valoran la elegancia
              discreta.
            </p>
          </FadeIn>

          <FadeIn delay={0.8} className="flex flex-wrap justify-center gap-3 mb-8" data-id="element-44">
            {['UDELAR', 'SAENI', 'UM', 'Carrasco, MVD'].map((badge, i) => <span key={i} className="px-4 py-1.5 border border-gold/30 rounded-full text-gold text-xs uppercase tracking-widest" data-id="element-45">
                {badge}
              </span>)}
          </FadeIn>

          <FadeIn delay={1.0} data-id="element-46">
            <button className="bg-gold text-forest px-6 py-3 rounded-full uppercase tracking-[0.15em] text-sm font-bold hover:bg-ivory transition-colors flex items-center gap-2 mx-auto group" data-id="element-47">
              Agendarme ahora
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-48" />
            </button>
          </FadeIn>
        </div>

        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-gold/50 to-transparent" data-id="element-49"></div>
      </section>

      {/* About Section */}
      <section id="sobre-mi" className="py-32 px-6 max-w-7xl mx-auto" data-id="element-50">
        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-51">
          <FadeIn data-id="element-52">
            <div className="relative aspect-[4/5] rounded-xl overflow-hidden" data-id="element-53">
              <div className="absolute inset-0 bg-forest/10 mix-blend-multiply z-10" data-id="element-54"></div>
              <img src="https://cdn.magicpatterns.com/uploads/sys3wP9ukB9sHkupKP2FTM/Mi_nombre_es_Carla,_medica_egresada_de_la_Universidad_de_la_Republica._Me_forme_en_Medicina_Este.jpg" alt="Dra. Carla Fierro" className="object-cover w-full h-full" data-id="element-55" />
              <div className="absolute bottom-6 left-6 right-6 bg-ivory/90 backdrop-blur-sm p-6 rounded-lg z-20 border border-gold/20" data-id="element-56">
                <p className="font-serif italic text-forest text-lg text-center" data-id="element-57">
                  "Realzar tu belleza natural, nunca reemplazarla."
                </p>
              </div>
            </div>
          </FadeIn>

          <div data-id="element-58">
            <FadeIn delay={0.2} data-id="element-59">
              <SectionLabel text="Sobre la doctora" data-id="element-60" />
              <h2 className="font-serif text-4xl md:text-5xl text-forest mb-4" data-id="element-61">
                Dra. Carla Fierro
              </h2>
              <p className="text-gold uppercase tracking-widest text-sm mb-8" data-id="element-62">
                Médica · Especialista en Medicina Estética
              </p>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-63">
              <p className="text-charcoal/80 leading-relaxed mb-10 text-lg" data-id="element-64">
                Formada en la Universidad de la República (UDELAR), con
                especialización en SAENI y UM, la Dra. Fierro combina rigor
                médico con sensibilidad estética para ofrecer resultados que
                realzan tu belleza natural — nunca la reemplazan.
              </p>
            </FadeIn>

            <div className="space-y-6" data-id="element-65">
              {[{
              icon: Stethoscope,
              text: 'Formación médica universitaria'
            }, {
              icon: Sparkles,
              text: 'Enfoque en resultados armónicos'
            }, {
              icon: MapPin,
              text: 'Atención personalizada en Carrasco'
            }].map((item, i) => <FadeIn key={i} delay={0.5 + i * 0.1} className="flex items-center gap-4" data-id="element-66">
                  <div className="w-10 h-10 rounded-full bg-forest/5 flex items-center justify-center text-forest" data-id="element-67">
                    <item.icon className="w-5 h-5" strokeWidth={1.5} data-id="element-68" />
                  </div>
                  <span className="text-charcoal font-medium" data-id="element-69">{item.text}</span>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="tratamientos" className="py-32 bg-forest px-6" data-id="element-70">
        <div className="max-w-7xl mx-auto" data-id="element-71">
          <div className="text-center mb-20" data-id="element-72">
            <FadeIn data-id="element-73">
              <SectionLabel text="Tratamientos" data-id="element-74" />
              <h2 className="font-serif text-4xl md:text-5xl text-ivory mb-6" data-id="element-75">
                Cada procedimiento,
                <br data-id="element-76" />
                diseñado para ti.
              </h2>
            </FadeIn>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-77">
            {[{
            title: 'Armonización Facial',
            desc: 'Rellenos y técnicas de reposicionamiento que devuelven equilibrio y frescura al rostro.',
            fullDesc: 'Utilizamos ácido hialurónico de primera línea para restaurar volúmenes perdidos, proyectar pómulos, definir el contorno mandibular y suavizar surcos. El objetivo es lograr proporciones ideales y un efecto lifting sin cirugía, respetando siempre la fisonomía original.',
            icon: Sparkles
          }, {
            title: 'Toxina Botulínica',
            desc: 'Resultados naturales que suavizan expresiones sin perder autenticidad.',
            fullDesc: 'Tratamiento preventivo y correctivo para las arrugas dinámicas (frente, entrecejo y patas de gallo). Relaja temporalmente los músculos faciales responsables de las líneas de expresión, brindando una mirada más descansada y una piel más lisa sin "congelar" el rostro.',
            icon: Syringe
          }, {
            title: 'Bioestimuladores',
            desc: 'Regeneración dérmica profunda para una piel más firme y luminosa desde adentro.',
            fullDesc: 'Inyectables (como Radiesse o Sculptra) que actúan en las capas profundas de la piel estimulando la producción natural de colágeno y elastina. Ideales para combatir la flacidez, mejorar la calidad de la piel y lograr un tensado progresivo y duradero.',
            icon: Activity
          }, {
            title: 'Skincare Médico',
            desc: 'Protocolos personalizados para el cuidado y mantenimiento de tu piel.',
            fullDesc: 'Evaluación exhaustiva de tu tipo de piel para diseñar una rutina de cuidado en casa con productos de grado médico. Incluye recomendaciones de activos específicos (retinoides, vitamina C, ácidos) para tratar manchas, acné, rosácea o envejecimiento prematuro.',
            icon: Droplets
          }, {
            title: 'Mesoterapia',
            desc: 'Nutrición celular dirigida para resultados visibles y progresivos.',
            fullDesc: 'Microinyecciones superficiales de cócteles personalizados (vitaminas, minerales, aminoácidos y ácido hialurónico no reticulado) que hidratan en profundidad, revitalizan la piel apagada y mejoran su textura y luminosidad desde la primera sesión.',
            icon: Syringe
          }, {
            title: 'Consulta de Valoración',
            desc: 'El primer paso para conocer tu caso y diseñar tu plan estético ideal.',
            fullDesc: 'Una evaluación médica completa donde analizamos tus necesidades, expectativas y anatomía facial. Diseñamos un plan de tratamiento a medida, explicándote detalladamente cada procedimiento, los productos a utilizar y los resultados esperados.',
            icon: Stethoscope
          }].map((service, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-78">
                <ServiceCard service={service} data-id="element-79" />
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-32 px-6 bg-forest border-t border-gold/10 relative overflow-hidden" data-id="element-80">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-81"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-82">
          <FadeIn data-id="element-83">
            <h2 className="font-serif text-3xl md:text-5xl text-gold italic leading-relaxed mb-8 text-balance" data-id="element-84">
              "La medicina estética bien hecha no cambia quien eres. Revela la
              mejor versión de ti."
            </h2>
            <p className="text-ivory uppercase tracking-[0.2em] text-sm mb-20" data-id="element-85">
              — Dra. Carla Fierro
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-10 border-t border-gold/20 pt-16" data-id="element-86">
            {[{
            icon: ShieldCheck,
            text: 'Diagnóstico médico preciso'
          }, {
            icon: Sparkles,
            text: 'Técnica artesanal y personalizada'
          }, {
            icon: Droplets,
            text: 'Resultados naturales y armónicos'
          }].map((pillar, i) => <FadeIn key={i} delay={0.2 + i * 0.1} className="flex flex-col items-center gap-4" data-id="element-87">
                <pillar.icon className="w-6 h-6 text-gold stroke-[1.5]" data-id="element-88" />
                <span className="text-ivory/90 text-sm uppercase tracking-wider" data-id="element-89">
                  {pillar.text}
                </span>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Technology & Social Proof */}
      <section className="py-32 px-6 max-w-7xl mx-auto" data-id="element-90">
        {/* Featured Technology Video Section */}
        <div className="bg-forest rounded-2xl overflow-hidden shadow-xl border border-gold/10 mb-32" data-id="element-91">
          <div className="grid md:grid-cols-2 items-center" data-id="element-92">
            <FadeIn className="h-full" data-id="element-93">
              <div className="relative h-full min-h-[400px] md:min-h-[500px]" data-id="element-94">
                <video src="https://cdn.magicpatterns.com/uploads/iaALRdtiEkhzWyWP7JLuGW/Cuando notás flacidez, el problema no está únicamente en la superficie.HIMFU con UNYQUE PRO trab.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-95" />
                <div className="absolute inset-0 bg-forest/10 mix-blend-multiply" data-id="element-96"></div>
              </div>
            </FadeIn>

            <div className="p-10 md:p-16 flex flex-col justify-center" data-id="element-97">
              <FadeIn delay={0.2} data-id="element-98">
                <SectionLabel text="Tecnología de Vanguardia" data-id="element-99" />
                <h3 className="font-serif text-3xl md:text-4xl text-ivory mb-6" data-id="element-100">
                  HIMFU con UNYQUE PRO
                </h3>
                <p className="text-ivory/80 font-light leading-relaxed mb-8 text-lg" data-id="element-101">
                  Cuando notas flacidez, el problema no está únicamente en la
                  superficie. Nuestro tratamiento con tecnología HIMFU
                  (Ultrasonido Multifocal de Alta Intensidad) trabaja en las
                  capas más profundas de la piel.
                </p>
                <ul className="space-y-4 mb-10" data-id="element-102">
                  {['Estimulación profunda de colágeno', 'Tensado cutáneo sin cirugía', 'Resultados visibles y progresivos'].map((item, i) => <li key={i} className="flex items-center gap-3 text-ivory/90" data-id="element-103">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold" data-id="element-104"></div>
                      <span data-id="element-105">{item}</span>
                    </li>)}
                </ul>
                <button className="border border-gold text-gold px-8 py-3 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-gold/10 transition-colors inline-flex items-center gap-2 w-fit" data-id="element-106">
                  Consultar por este tratamiento
                  <ArrowRight className="w-4 h-4" data-id="element-107" />
                </button>
              </FadeIn>
            </div>
          </div>
        </div>

        <div className="text-center mb-20" data-id="element-108">
          <FadeIn data-id="element-109">
            <SectionLabel text="Lo que dicen mis pacientes" data-id="element-110" />
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-111">
          {[1, 2, 3].map((_, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-112">
              <div className="bg-white p-10 rounded-xl shadow-[0_4px_20px_-4px_rgba(45,74,62,0.05)] border border-forest/5 h-full flex flex-col" data-id="element-113">
                <div className="flex gap-1 mb-6" data-id="element-114">
                  {[1, 2, 3, 4, 5].map(star => <Sparkles key={star} className="w-4 h-4 text-gold fill-gold/20" data-id="element-115" />)}
                </div>
                <p className="text-forest/80 italic leading-relaxed mb-8 flex-grow" data-id="element-116">
                  "Llegué buscando un cambio sutil y salí con una versión más
                  fresca y natural de mí misma. La Dra. Fierro escucha y trabaja
                  con precisión."
                </p>
                <p className="text-charcoal font-bold text-sm uppercase tracking-wider" data-id="element-117">
                  — M.G., Carrasco
                </p>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* CTA / Booking Block */}
      <section id="contacto" className="max-w-7xl mx-auto px-6 mb-32" data-id="element-118">
        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row shadow-2xl" data-id="element-119">
          {/* Left Side */}
          <div className="bg-forest p-12 md:p-20 md:w-3/5 flex flex-col justify-center" data-id="element-120">
            <FadeIn data-id="element-121">
              <SectionLabel text="Reservá tu consulta" data-id="element-122" />
              <h2 className="font-serif text-4xl md:text-5xl text-ivory mb-6 text-balance" data-id="element-123">
                Tu transformación comienza con una conversación.
              </h2>
              <p className="text-ivory/80 mb-12 text-lg font-light" data-id="element-124">
                Atención personalizada en Carrasco, Montevideo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-125">
                <button className="bg-gold text-forest px-8 py-4 rounded-full uppercase tracking-[0.15em] text-sm font-bold hover:bg-ivory transition-colors flex items-center justify-center gap-2" data-id="element-126">
                  Escribirme por WhatsApp <ArrowRight className="w-4 h-4" data-id="element-127" />
                </button>
                <button className="border border-gold text-gold px-8 py-4 rounded-full uppercase tracking-[0.15em] text-sm font-bold hover:bg-gold/10 transition-colors flex items-center justify-center" data-id="element-128">
                  Ver Instagram
                </button>
              </div>
            </FadeIn>
          </div>

          {/* Right Side */}
          <div className="bg-white p-12 md:p-20 md:w-2/5 flex flex-col justify-center border-y border-r border-forest/10" data-id="element-129">
            <FadeIn delay={0.2} className="space-y-10" data-id="element-130">
              <div data-id="element-131">
                <p className="text-gold uppercase tracking-widest text-xs font-bold mb-2" data-id="element-132">
                  Ubicación
                </p>
                <p className="text-forest font-serif text-xl" data-id="element-133">
                  Carrasco, Montevideo
                  <br data-id="element-134" />
                  Uruguay
                </p>
              </div>
              <div data-id="element-135">
                <p className="text-gold uppercase tracking-widest text-xs font-bold mb-2" data-id="element-136">
                  Social
                </p>
                <p className="text-forest font-serif text-xl" data-id="element-137">
                  @dracarlafierro
                </p>
              </div>
              <div data-id="element-138">
                <p className="text-gold uppercase tracking-widest text-xs font-bold mb-2" data-id="element-139">
                  Credenciales
                </p>
                <p className="text-forest font-serif text-xl" data-id="element-140">
                  UDELAR · SAENI · UM
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-forest pt-20 pb-10 px-6 text-center" data-id="element-141">
        <FadeIn data-id="element-142">
          <Monogram className="mx-auto mb-8" data-id="element-143" />
          <h3 className="font-serif text-2xl text-ivory mb-2" data-id="element-144">
            Dra. Carla Fierro | Medicina Estética
          </h3>
          <p className="text-ivory/60 uppercase tracking-widest text-sm mb-12" data-id="element-145">
            Carrasco, Montevideo · Uruguay
          </p>

          <div className="w-24 h-px bg-gold/30 mx-auto mb-12" data-id="element-146"></div>

          <p className="text-ivory/40 text-xs uppercase tracking-widest" data-id="element-147">
            © {new Date().getFullYear()} · Todos los derechos reservados
          </p>
        </FadeIn>
      </footer>
    </div>;
}